/**
 * =============================================================================
 * WlFinancialOverviewTab
 *
 * Aba "Visao Geral" do painel WL -> Financeiro. Mostra 8 KPIs clicaveis,
 * agrupamentos por plano/add-on e graficos evolutivos (12 meses). Clique
 * em qualquer KPI/linha abre WlFinancialDetailModal.
 *
 * @version 1.7.0
 * RELEASE:
 *   2026-07-21 v1.0.0 - Versao inicial.
 *   2026-07-21 v1.1.0 - Grid de KPIs em 3 colunas; pizzas "Receita por
 *                       plano/add-on"; breakdowns em linhas independentes.
 *   2026-07-21 v1.2.0 - Layout 2x2 + feed "Últimos Recebimentos".
 *   2026-07-21 v1.3.0 - Feed "Últimos Recebimentos" na linha das cobranças.
 *   2026-07-21 v1.4.0 - Filtro multi-select de status (default: Ativo).
 *   2026-07-21 v1.5.0 - `statuses` passa a vir do pai (header da pagina).
 *                       Toggle bar/line movido para o CardHeader dos graficos
 *                       (mode controlado). Removido bloco de filtro interno.
 *   2026-07-22 v1.6.0 - Card "Novo MRR (mês)" clicável abrindo drill-down
 *                       de tenants criados no mês corrente (SP) via RPC v1.7.0.
 *   2026-07-22 v1.7.0 - Removidos cards "Distribuição do MRR por add-on" e
 *                       "Receita por add-on" — migrados para a aba Add-ons.
 *                       Grid inferior fica só com "por plano" (linha única).
 * =============================================================================
 */
import { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  Package,
  PlusCircle,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CrmPremiumKpiCard } from '@/components/crm/dashboard/premium';
import { useWlFinancialKpis } from '@/hooks/wl/useWlFinancialKpis';
import {
  WlFinancialDetailModal,
  WlFinancialDetailType,
} from './WlFinancialDetailModal';
import { WlRevenueTrendChart } from './charts/WlRevenueTrendChart';
import { WlTenantsTrendChart } from './charts/WlTenantsTrendChart';
import { WlRevenueByPlanPieChart } from './charts/WlRevenueByPlanPieChart';
// v1.7.0: WlRevenueByAddonPieChart movido para a aba Add-ons.
import { WlRecentContractsCard } from './WlRecentContractsCard';
import { WlRecentPaymentsFeed } from './WlRecentPaymentsFeed';
import { ChartTypeToggle, ChartTypeMode } from './charts/ChartTypeToggle';
import { getCurrentMonthRangeSP } from './utils/monthRangeSP';

const fmtBRL = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v || 0));

interface ModalState {
  open: boolean;
  type: WlFinancialDetailType;
  title: string;
  planId?: string | null;
  addonId?: string | null;
  /** Status efetivos vindos do filtro do overview (fonte unica de verdade). */
  statuses?: string[];
  /** v1.6.0: janelas de período para drill-down mensal. */
  createdFrom?: string | null;
  createdTo?: string | null;
  canceledFrom?: string | null;
  canceledTo?: string | null;
  periodOr?: boolean;
}

interface Props {
  /** Filtro de status controlado pelo pai (header da pagina). */
  statuses: string[];
}

/** Aba Visao Geral do dashboard financeiro. */
export function WlFinancialOverviewTab({ statuses }: Props) {
  const { data, isLoading } = useWlFinancialKpis(null, 'America/Sao_Paulo', statuses);
  const [modal, setModal] = useState<ModalState>({ open: false, type: 'mrr', title: '' });

  // Modo dos graficos evolutivos — controlado aqui para expor o toggle no CardHeader.
  const [revenueChartMode, setRevenueChartMode] = useState<ChartTypeMode>('line');
  const [tenantsChartMode, setTenantsChartMode] = useState<ChartTypeMode>('line');

  /** Helper para abrir modal parametrizado — sempre propaga statuses. */
  const openModal = (partial: Omit<ModalState, 'open' | 'statuses'> & { statuses?: string[] }) =>
    setModal({ ...partial, statuses: partial.statuses ?? statuses, open: true });

  const k = data?.kpis;

  return (
    <div className="space-y-6">
      {/* ============================= KPIs ================================
        v2.0.0: removidos 4 cards duplicados (Tenants pagantes, Ticket medio,
        Receita em risco, Churn de receita — ja cobertos em outras abas).
        Migrado para CrmPremiumKpiCard (mesmo padrao da aba Cobranca). */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {isLoading || !k ? (
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[130px] w-full" />
          ))
        ) : (
          <>
            <CrmPremiumKpiCard
              title="MRR"
              value={fmtBRL(k.mrr.value ?? 0)}
              icon={DollarSign}
              variant="success"
              progress={(k.mrr.value ?? 0) > 0 ? 90 : 0}
              tooltip="Receita Recorrente Mensal (Monthly Recurring Revenue) — fonte única de verdade da recorrência. Soma normalizada em base mensal dos assinantes pagantes ativos, considerando planos e add-ons recorrentes (planos anuais dividem por 12, semestrais por 6, etc.). NÃO é comparável ao Receita Total da aba Cobrança: esse último mostra faturamento bruto do mês por vencimento (planos + add-ons + avulsos)."
              onClick={() => openModal({ type: 'mrr', title: 'MRR — Tenants pagantes' })}
            />
            <CrmPremiumKpiCard
              title="ARR"
              value={fmtBRL(k.arr.value ?? 0)}
              icon={TrendingUp}
              variant="purple"
              progress={(k.arr.value ?? 0) > 0 ? 90 : 0}
              tooltip="Receita Recorrente Anual (Annual Recurring Revenue) = MRR x 12. Projecao anualizada da receita recorrente atual."
              onClick={() => openModal({ type: 'mrr', title: 'ARR — Tenants pagantes' })}
            />
            <CrmPremiumKpiCard
              title="Novo MRR (mês)"
              value={fmtBRL(k.nmrr.value ?? 0)}
              icon={PlusCircle}
              variant="info"
              progress={
                (k.mrr.value ?? 0) > 0
                  ? Math.max(0, Math.min(100, Math.round(((k.nmrr.value ?? 0) / k.mrr.value) * 100)))
                  : 0
              }
              tooltip="MRR novo contratado no mes corrente (assinantes que passaram a ser pagantes no periodo). Indica expansao real da receita."
              onClick={() => {
                // v1.6.0: drill-down para tenants criados no mês corrente (SP).
                const { startISO, endISO, label } = getCurrentMonthRangeSP();
                openModal({
                  type: 'paying',
                  title: `Novo MRR — ${label}`,
                  statuses: ['active'],
                  createdFrom: startISO,
                  createdTo: endISO,
                });
              }}
            />
            <CrmPremiumKpiCard
              title={
                k.addons_total.count
                  ? `Add-ons · ${k.addons_total.count} contratados`
                  : 'Add-ons'
              }
              value={fmtBRL(k.addons_total.value ?? 0)}
              icon={Package}
              variant="warning"
              progress={
                (k.mrr.value ?? 0) > 0
                  ? Math.max(0, Math.min(100, Math.round(((k.addons_total.value ?? 0) / k.mrr.value) * 100)))
                  : 0
              }
              tooltip="MRR total gerado pelos add-ons contratados no periodo (receita complementar aos planos)."
              onClick={() => openModal({ type: 'addon', title: 'Add-ons contratados' })}
            />
          </>
        )}
      </div>


      {/* ============================= Charts ============================== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium">Evolução do MRR (12 meses)</CardTitle>
            <ChartTypeToggle value={revenueChartMode} onChange={setRevenueChartMode} />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Carregando…</p>
            ) : (
              <WlRevenueTrendChart
                data={data?.trend_revenue ?? []}
                mode={revenueChartMode}
                onModeChange={setRevenueChartMode}
              />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
            <CardTitle className="text-sm font-medium">Novos tenants vs. churn</CardTitle>
            <ChartTypeToggle value={tenantsChartMode} onChange={setTenantsChartMode} />
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Carregando…</p>
            ) : (
              <WlTenantsTrendChart
                data={data?.trend_tenants ?? []}
                mode={tenantsChartMode}
                onModeChange={setTenantsChartMode}
              />
            )}
          </CardContent>
        </Card>
      </div>


      {/*
        ==================== v1.3.0 — Grid 2x2 plano/add-on ====================
        Layout 2 colunas: pizza + tabela para plano (linha 1) e add-on (linha 2).
        O feed "Últimos Recebimentos" foi movido para a linha das cobranças
        recentes (1/3), para melhor aproveitamento vertical.
      */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* --- Linha 1 esquerda: Distribuição por plano --------------------- */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Distribuição do MRR por plano</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-sm text-muted-foreground">Carregando…</p>
            ) : (
              <WlRevenueByPlanPieChart data={data?.by_plan ?? []} />
            )}
          </CardContent>
        </Card>

        {/* --- Linha 1 direita: Receita por plano (tabela) ------------------ */}
        <Card className="flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Receita por plano</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="max-h-[300px] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead className="text-xs font-medium">Plano</TableHead>
                    <TableHead className="text-xs font-medium text-center">Tenants</TableHead>
                    <TableHead className="text-xs font-medium text-right">MRR</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(data?.by_plan ?? []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-6 text-sm text-muted-foreground">
                        Sem dados
                      </TableCell>
                    </TableRow>
                  ) : (
                    data!.by_plan.map((row) => (
                      <TableRow
                        key={row.plan_id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() =>
                          openModal({
                            type: 'plan',
                            title: `Plano: ${row.plan_name}`,
                            planId: row.plan_id,
                          })
                        }
                      >
                        <TableCell className="py-2 text-xs font-medium">{row.plan_name}</TableCell>
                        <TableCell className="py-2 text-xs text-center">{row.tenant_count}</TableCell>
                        <TableCell className="py-2 text-xs text-right">{fmtBRL(row.mrr)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

      </div>


      {/* ============ Contratações recentes (2/3) + Últimos recebimentos (1/3) ============ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 min-w-0">
          <WlRecentContractsCard statuses={statuses} />
        </div>
        <div className="lg:col-span-1 min-h-[300px]">
          <WlRecentPaymentsFeed />
        </div>
      </div>






      {/* =========================== Detail Modal ========================== */}
      <WlFinancialDetailModal
        open={modal.open}
        onOpenChange={(open) => setModal((m) => ({ ...m, open }))}
        type={modal.type}
        title={modal.title}
        planId={modal.planId}
        addonId={modal.addonId}
        statuses={modal.statuses}
        createdFrom={modal.createdFrom}
        createdTo={modal.createdTo}
        canceledFrom={modal.canceledFrom}
        canceledTo={modal.canceledTo}
        periodOr={modal.periodOr}
      />
    </div>
  );
}
